import {View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {gradeMetaWords, subjectLabel} from '#/lib/curriculum'
import {type DocumentAudience} from '#/lib/document/types'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

/**
 * How a document's audience is named, in one place for every surface that
 * shows it.
 *
 * A published document carries `Level_B1` and `Maths` — codes, because the
 * record is read in languages its author never wrote in. Naming them is the
 * reader's job, and this is where that happens for a document: the card, the
 * reader's header and the composer all call in here, so one document is
 * described the same way wherever it is seen.
 *
 * Two deliberate refusals, both earned by looking at what came out:
 *
 * - No bare grade. Three stages contain a "2nd year", so the stage travels with
 *   it: `Primary · 2nd year`, `Secondary · BAC`. A reader handed only the year
 *   cannot tell which one they are looking at.
 * - No grade emoji. A subject's emoji is a topic glyph — a bulb for physics
 *   reads at a glance, which is why a FlashBook card wears one too — but the
 *   grades are emoji *keycaps*, and mid-line a keycap renders as a blue badge
 *   where a separator belongs: `Physics · [4] BAC` reads as one mangled word.
 *   So the subject keeps its glyph and the level is spelled out.
 */
export function DocumentAudienceLine({
  audience,
}: {
  audience?: DocumentAudience
}) {
  const {t: l} = useLingui()
  const t = useTheme()
  if (!audience) return null
  const subject = subjectLabel(audience.subject)
  const parts = [
    subject ? l(subject) : null,
    gradeText(audience.level, l),
  ].filter(Boolean)
  if (!parts.length) return null

  return (
    <Text style={[a.text_xs, t.atoms.text_contrast_medium]} numberOfLines={1}>
      {parts.join(' · ')}
    </Text>
  )
}

export function DocumentAudienceChips({
  audience,
}: {
  audience?: DocumentAudience
}) {
  const {t: l} = useLingui()
  const subject = subjectLabel(audience?.subject)
  const level = audience ? gradeText(audience.level, l) : ''
  if (!subject && !level) return null

  return (
    <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
      {subject && <Chip label={l(subject)} emphasis />}
      {level && <Chip label={level} />}
    </View>
  )
}

/** "Secondary · BAC", in the reader's own language. Empty when unrecognised. */
function gradeText(
  level: string,
  l: ReturnType<typeof useLingui>['t'],
): string {
  return gradeMetaWords(level)
    .map(word => l(word))
    .join(' · ')
}

function Chip({label, emphasis}: {label: string; emphasis?: boolean}) {
  const t = useTheme()
  return (
    <View
      style={[
        a.px_xs,
        a.py_2xs,
        a.rounded_xs,
        emphasis ? t.atoms.bg_contrast_50 : t.atoms.bg_contrast_25,
      ]}>
      <Text
        style={[
          a.text_2xs,
          emphasis ? a.font_bold : null,
          emphasis ? t.atoms.text : t.atoms.text_contrast_medium,
        ]}>
        {label}
      </Text>
    </View>
  )
}
